import { useEffect, useState, useMemo} from 'react'
import { Link } from 'react-router-dom'
import { CalendarCheck, Check, CheckCheck, ChevronDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import { useAuth } from '../hooks/useAuth'
import { closedMessage, fmtUk, resolveWindow } from '../lib/reportingWindow'
import LoadingSpinner from '../components/LoadingSpinner'
import InfoTooltip from '../components/InfoTooltip'
import Modal from '../components/Modal'
import ReportingDiscipline from '../components/ReportingDiscipline'
import { monthStanding } from '../lib/lateReports'
import { formatDate, formatNumber, forecastOnTime } from '../lib/utils'
import { ragCounts, hasMaterialOpenRisk, DEFAULT_RISK_FORECAST_MIN_SCORE, RAG_BAR, RAG_TEXT, RAG_LABEL, type RiskRag } from '../lib/risk'
import type { Project, MonthReview, ProjectReport, Settings } from '../types'

// Local (not UTC) month key so a validation ticked late on the 31st doesn't
// land in next month's doc for UK/EU users.
const currentMonthKey = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const shiftMonth = (key: string, delta: number) => {
  const [y, m] = key.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const monthLabelOf = (key: string) => {
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

// Open-risk counts by RAG band - shows only the bands present, a muted dash when clean.
function RiskDots({ project }: { project: Project }) {
  const counts = ragCounts(project.risks ?? [])
  const order: RiskRag[] = ['red', 'amber', 'green']
  const total = order.reduce((s, b) => s + counts[b], 0)
  if (total === 0) return <span className="text-xs text-slate-300">None</span>
  return (
    <div className="inline-flex items-center gap-2">
      {order.map(band => counts[band] > 0 && (
        <span key={band} className={`inline-flex items-center gap-1 text-xs font-semibold ${RAG_TEXT[band]}`}
          title={`${counts[band]} open ${RAG_LABEL[band]} risk${counts[band] !== 1 ? 's' : ''}`}>
          <span className={`w-2 h-2 rounded-full ${RAG_BAR[band]}`} />
          {counts[band]}
        </span>
      ))}
    </div>
  )
}

// Forecast OTIF pills for an in-flight project: green = on track, red = at risk.
// Mirrors the Reports forecast rules - booked go-live vs due date + material open risks.
function OtifBadges({ project, onTimeMs, minScore }: { project: Project; onTimeMs: number; minScore: number }) {
  const cells: [string, string, boolean][] = [
    ['OT', 'On Time', forecastOnTime(project, onTimeMs).late || hasMaterialOpenRisk(project.risks, 'delivery', minScore)],
    ['IF', 'In Full', hasMaterialOpenRisk(project.risks, 'scope', minScore)],
    ['AC', 'At Cost', hasMaterialOpenRisk(project.risks, 'cost', minScore)],
  ]
  return (
    <div className="inline-flex items-center gap-1">
      {cells.map(([label, full, atRisk]) => (
        <span key={label} title={`${full}: ${atRisk ? 'at risk' : 'on track'}`}
          className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${atRisk ? 'bg-[#EF3340]/10 text-[#B8242E]' : 'bg-[#00843D]/10 text-[#00843D]'}`}>
          {label}
        </span>
      ))}
    </div>
  )
}

/**
 * A project's status report for the period, and the sign-off control.
 *
 * Only projects opted into reporting show anything here - the rest report
 * nothing, and a row of "n/a" across most of the table would be noise.
 *
 * The states map to what the reviewer can actually do: nothing filed yet or
 * still a draft is the PM's move, so those link into the editor rather than
 * offering a button that would sign off an unfinished report. Submitted is the
 * only state that invites validation.
 */
function ReportCell({ project, report, month, dueAt, editable, onToggle }: {
  project: Project
  report?: ProjectReport
  month: string
  /** The month's due instant as it stands now. */
  dueAt: Date | null
  editable: boolean
  onToggle: () => void
}) {
  const href = `/projects/${project.id}/report?month=${month}`

  if (!project.reportable) return <span className="text-xs text-slate-300">-</span>

  if (!report) {
    return (
      <Link to={href} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-xs font-semibold text-slate-400 hover:border-[#F88806]/50 hover:text-[#B8640B]">
        Not started
      </Link>
    )
  }

  if (report.state === 'draft') {
    return (
      <Link to={href} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#F88806]/10 text-[#B8640B] text-xs font-semibold hover:bg-[#F88806]/20"
            title="Still a draft - the project manager has not submitted it">
        Draft
      </Link>
    )
  }

  // The due date can move after reports are in, so lateness is judged against
  // the due date the header shows. The flag stamped at submission only answers
  // when there is no due date or submission time to compare.
  const late = dueAt && report.submittedAt
    ? new Date(report.submittedAt).getTime() > dueAt.getTime()
    : !!report.submittedAfterDue
  const afterCutOff = late && (
    <span title={report.submittedAt ? `Submitted ${fmtUk(new Date(report.submittedAt))}, after the month's cut-off` : "Submitted after the month's cut-off"}
          className="ml-1.5 text-[10px] font-semibold text-[#B8640B]">
      after cut-off
    </span>
  )

  if (report.state === 'submitted') {
    return editable ? (
      <>
        <button onClick={onToggle}
                title="Validate this month's status report"
                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-[#005EB8]/30 text-xs font-semibold text-[#005EB8] hover:bg-[#005EB8]/10">
          Validate report
        </button>
        {afterCutOff}
      </>
    ) : (
      <>
        <span className="text-xs text-slate-400">Submitted</span>
        {afterCutOff}
      </>
    )
  }

  return (
    <>
      <button onClick={onToggle}
              disabled={!editable}
              title={editable ? 'Click to reopen for changes' : 'Historical record'}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#00843D]/10 text-[#00843D] text-xs font-semibold ${editable ? 'hover:bg-[#00843D]/20' : 'cursor-default'}`}>
        <Check className="w-3 h-3" />
        Report{report.reviewedBy?.name ? ` · ${report.reviewedBy.name}` : ''}
      </button>
      {afterCutOff}
    </>
  )
}

export default function MonthEndReview() {
  useDocumentTitle('End of Month Review')
  const api = useApi()
  const [month, setMonth] = useState(currentMonthKey)
  const isCurrentMonth = month === currentMonthKey()
  const { user } = useAuth()
  const isAdmin = !!user?.isAdmin

  const { data, loading, refetch } = useData(
    () =>
      Promise.all([
        api.getProjects(), api.getMonthReview(month), api.getSettings(), api.getProjectReports(month),
      ]).then(([projects, review, settings, reports]) => ({
        projects: projects as Project[],
        review: review as MonthReview,
        settings: settings as Settings,
        reports: reports as ProjectReport[],
      })),
    [month]
  )

  const [review, setReview] = useState<MonthReview | null>(null)
  // Project validation ticks keep the rule they always had: the current month
  // is always open, and a past month stays open while its reporting window
  // (or the default 7-day grace) is. Report sign-off is per project below,
  // where the backend enforces the window; the tick itself is browser-only.
  const monthWindow = useMemo(() => resolveWindow(month, review), [month, review])
  const isEditable = isAdmin || isCurrentMonth || monthWindow.isOpen
  const reportEditable = (projectId: string) => isAdmin || resolveWindow(month, review, projectId).isOpen
  // Reports are held locally too, so signing one off updates the row without
  // refetching the whole month.
  const [reports, setReports] = useState<Record<string, ProjectReport>>({})
  const [errorModal, setErrorModal] = useState<string | null>(null)
  const [hideValidated, setHideValidated] = useState(false)
  // Explicit user overrides for section collapse; teams without an entry
  // auto-collapse once fully validated.
  const [collapseOverride, setCollapseOverride] = useState<Record<string, boolean>>({})
  const [bulkTeam, setBulkTeam] = useState<string | null>(null)
  useEffect(() => { if (data) setReview(data.review) }, [data])
  useEffect(() => {
    if (data) setReports(Object.fromEntries((data.reports ?? []).map(r => [r.projectId, r])))
  }, [data])
  useEffect(() => { setCollapseOverride({}); setHideValidated(false) }, [month])

  if (loading || !data || !review || review.id !== month) return <LoadingSpinner />

  const allProjects = data.projects || []
  const inFlight = allProjects.filter(p => p.status === 'forecast')
  // Forecast OTIF thresholds, same knobs the Reports forecast view uses.
  const onTimeMs = (data.settings?.onTimeDays ?? 14) * 24 * 60 * 60 * 1000
  const minScore = data.settings?.riskForecastMinScore ?? DEFAULT_RISK_FORECAST_MIN_SCORE

  // Current month: every in-flight project needs a decision. Past months:
  // also include projects that were validated back then but have since moved
  // on (completed/cancelled), so the historical record stays complete.
  const byId = new Map(allProjects.map(p => [p.id, p]))
  const rows = new Map<string, Project>(inFlight.map(p => [p.id, p]))
  if (!isCurrentMonth) {
    for (const id of Object.keys(review.reviews)) {
      const p = byId.get(id)
      if (p) rows.set(id, p)
    }
  }

  // Group by team; teams alphabetical with Unassigned last. Within a group,
  // needs-attention first: unvalidated before validated, stalest first.
  const byTeam = new Map<string, Project[]>()
  for (const p of rows.values()) {
    const team = p.team || 'Unassigned'
    byTeam.set(team, [...(byTeam.get(team) || []), p])
  }
  const teams = [...byTeam.keys()].sort((a, b) =>
    a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b))
  for (const team of teams) {
    byTeam.get(team)!.sort((a, b) => {
      const av = review.reviews[a.id] ? 1 : 0
      const bv = review.reviews[b.id] ? 1 : 0
      if (av !== bv) return av - bv
      return (a.lastMeaningfulUpdate || '').localeCompare(b.lastMeaningfulUpdate || '')
    })
  }

  const rowList = [...rows.values()]
  const validatedCount = rowList.filter(p => review.reviews[p.id]).length
  const allDone = rowList.length > 0 && validatedCount === rowList.length

  // Report sign-off runs alongside project validation, over the subset that
  // actually files one. Counted separately because a project can be data-checked
  // without its report being ready, and both need to land before month end.
  const reportRows = rowList.filter(p => p.reportable)
  const reportsValidated = reportRows.filter(p => reports[p.id]?.state === 'reviewed').length

  // Who filed late this month. Reads the reports held locally, so it stays
  // right as they are signed off.
  const standing = monthStanding(month, allProjects, review, Object.values(reports), new Date(), isCurrentMonth)

  /**
   * Sign a project's status report off, or reopen it.
   *
   * Uses the report's own draft -> submitted -> reviewed lifecycle rather than
   * a second mark in the month-review doc: the editor already drives that
   * lifecycle, and a parallel flag would be a second source of truth to keep
   * in step. Only the content-free state endpoint is called, so validating
   * here can never overwrite something the PM edited in the meantime.
   */
  const toggleReportReview = async (p: Project) => {
    const current = reports[p.id]
    if (!current) return
    const next = current.state === 'reviewed' ? 'submitted' : 'reviewed'
    const before = current
    setReports(r => ({ ...r, [p.id]: { ...current, state: next } }))
    try {
      const updated = await api.setProjectReportState(month, p.id, next) as ProjectReport
      setReports(r => ({ ...r, [p.id]: updated }))
    } catch (e) {
      setReports(r => ({ ...r, [p.id]: before }))
      setErrorModal(e instanceof Error ? e.message : 'Could not update the report.')
    }
  }

  const toggleReview = async (p: Project) => {
    if (!isEditable) return // history (beyond the grace window) is read-only
    const wasValidated = !!review.reviews[p.id]
    const prev = review
    // Optimistic flip; server response (the full doc) settles the final state.
    const optimistic: MonthReview = { ...review, reviews: { ...review.reviews } }
    if (wasValidated) delete optimistic.reviews[p.id]
    else optimistic.reviews[p.id] = { by: {} as any, at: new Date().toISOString() }
    setReview(optimistic)
    try {
      const updated = wasValidated
        ? await api.clearMonthReview(month, p.id)
        : await api.setMonthReview(month, p.id)
      setReview(updated as MonthReview)
    } catch (e: any) {
      setReview(prev)
      setErrorModal(e?.message || 'Failed to update validation')
    }
  }

  // Bulk-validate a team's remaining projects. Requests run SEQUENTIALLY on
  // purpose: the backend does read-modify-write on one month doc, so parallel
  // PUTs would race and drop marks.
  const validateRemaining = async (team: string) => {
    if (!isEditable || bulkTeam) return
    const pending = (byTeam.get(team) || []).filter(p => !review.reviews[p.id])
    if (pending.length === 0) return
    setBulkTeam(team)
    const optimistic: MonthReview = { ...review, reviews: { ...review.reviews } }
    for (const p of pending) optimistic.reviews[p.id] = { by: {} as any, at: new Date().toISOString() }
    setReview(optimistic)
    try {
      let latest: MonthReview = review
      for (const p of pending) {
        latest = (await api.setMonthReview(month, p.id)) as MonthReview
      }
      setReview(latest)
    } catch (e: any) {
      setErrorModal(e?.message || 'Bulk validation was interrupted - reloading the saved state.')
      refetch()
    } finally {
      setBulkTeam(null)
    }
  }

  const monthLabel = monthLabelOf(month)

  return (
    <div className="max-w-7xl">
      <Modal open={!!errorModal} title="Update Failed" message={errorModal || ''} onConfirm={() => setErrorModal(null)} />

      {/* Sticky header: month nav + progress stay visible while working down
          a long list. -top-8 cancels the page's p-8 so it sits flush. Uses the
          page background colour (#F0F9FF) so it's invisible until content
          actually scrolls beneath it. */}
      <div className="sticky -top-8 z-10 -mx-2 px-2 pt-2 pb-3 mb-3 bg-[#F0F9FF]/90 backdrop-blur-sm">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              End of Month Review
              <InfoTooltip>
                <strong>End of month data check</strong>
                <p>Every in-flight (forecast) project, grouped by team. Check the data is current - due date, costs, expected value - then mark it validated for the month.</p>
                <p>Each row surfaces the due date, forecast go-live, annual value (hrs), open risks by RAG, and the forecast OTIFAC landing (OT / IF / AC - green on track, red at risk) so you can spot-check without opening the project.</p>
                <p>Projects opted into monthly reporting carry a second sign-off: once the project manager submits the status report, it can be validated here too. Drafts and unstarted reports link through to the editor.</p>
                <p>Validations record who signed off and when, and reset automatically at the start of each month. Use the arrows to look back at previous months.</p>
                <p className="text-slate-300">Completed projects aren't listed for the current month - their numbers were confirmed when they were marked complete.</p>
              </InfoTooltip>
            </h1>
            <p className="text-slate-500 text-sm">Confirm in-flight project data is up to date</p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideValidated}
                onChange={e => setHideValidated(e.target.checked)}
                className="w-3.5 h-3.5 rounded"
                style={{ accentColor: '#005EB8' }}
              />
              Hide validated
            </label>
            {/* Month navigation - future months don't exist yet, so the forward
                arrow stops at the current month. Past months are read-only. */}
            <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setMonth(m => shiftMonth(m, -1))}
                title="Previous month"
                className="px-2 py-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1.5 text-sm font-semibold text-slate-700 min-w-[130px] text-center select-none">
                {monthLabel}
              </span>
              <button
                type="button"
                onClick={() => setMonth(m => shiftMonth(m, 1))}
                disabled={isCurrentMonth}
                title="Next month"
                className="px-2 py-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className={`text-sm font-semibold ${allDone ? 'text-[#00843D]' : 'text-slate-600'}`}>
                  {validatedCount}/{rowList.length} validated
                </div>
                <div className="w-40 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#00843D] transition-all"
                    style={{ width: rowList.length ? `${(validatedCount / rowList.length) * 100}%` : '0%' }}
                  />
                </div>
              </div>
              {reportRows.length > 0 && (
                <div className="flex items-center gap-2 pl-4 border-l border-slate-200"
                     title="Status reports signed off, across the projects that file one">
                  <div className={`text-sm font-semibold ${
                    reportsValidated === reportRows.length ? 'text-[#00843D]' : 'text-slate-600'}`}>
                    {reportsValidated}/{reportRows.length}
                  </div>
                  <span className="text-xs text-slate-400">reports</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`mb-4 flex items-start gap-2 px-3 py-2 rounded-lg text-xs border ${
        monthWindow.isOpen ? 'bg-[#00ACED]/5 border-[#00ACED]/30 text-slate-700'
          : 'bg-[#F88806]/5 border-[#F88806]/30 text-[#7A4A00]'}`}>
        <CalendarCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold">Reporting window:</span>{' '}
          opens {fmtUk(monthWindow.opensAt, false)} ·{' '}
          {monthWindow.dueAt
            ? <>due {fmtUk(monthWindow.dueAt)} · locks {fmtUk(monthWindow.closesAt)}</>
            : <>closes {fmtUk(monthWindow.closesAt)}</>}
          {monthWindow.source === 'default' && <span className="text-slate-500"> (default rule, no window set)</span>}
          {review.reportingWindow?.note && <span className="text-slate-500"> · {review.reportingWindow.note}</span>}
          {!isEditable && (
            <div className="mt-1">
              {closedMessage(month, monthWindow)}
              {' Validation for this month is closed.'}
            </div>
          )}
        </div>
      </div>

      <ReportingDiscipline current={standing} />

      {rowList.length === 0 ? (
        <div className="card p-10 text-center text-slate-400">
          <CalendarCheck className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          {isCurrentMonth
            ? 'No in-flight projects to validate - the pipeline is empty.'
            : `Nothing was validated in ${monthLabel}.`}
        </div>
      ) : (
        <div className="space-y-3">
          {teams.map(team => {
            const teamRows = byTeam.get(team)!
            const teamValidated = teamRows.filter(p => review.reviews[p.id]).length
            const teamDone = teamValidated === teamRows.length
            // Fully-validated teams fold away so the remaining work stands out;
            // an explicit click always wins over the auto behaviour.
            const isCollapsed = collapseOverride[team] ?? teamDone
            const visibleRows = hideValidated ? teamRows.filter(p => !review.reviews[p.id]) : teamRows
            const isBulking = bulkTeam === team
            return (
              <div key={team} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <button
                    type="button"
                    onClick={() => setCollapseOverride(o => ({ ...o, [team]: !isCollapsed }))}
                    className="flex items-center gap-2 flex-1 text-left group"
                  >
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isCollapsed ? '-rotate-90' : ''}`} />
                    <span className="flex items-baseline gap-2">
                      <h2 className="font-bold text-slate-800 group-hover:text-danone-blue">{team}</h2>
                      {teamDone ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#00843D]">
                          <Check className="w-3.5 h-3.5" /> {teamValidated}/{teamRows.length}
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-slate-500">{teamValidated}/{teamRows.length} validated</span>
                      )}
                    </span>
                  </button>
                  {isEditable && !teamDone && (
                    <button
                      type="button"
                      onClick={() => validateRemaining(team)}
                      disabled={!!bulkTeam}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[#00843D]/30 text-xs font-semibold text-[#00843D] hover:bg-[#00843D]/10 disabled:opacity-50"
                    >
                      {isBulking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
                      Validate remaining ({teamRows.length - teamValidated})
                    </button>
                  )}
                </div>
                {!isCollapsed && visibleRows.length > 0 && (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                        <th className="w-2" />
                        <th className="py-1.5 pr-3 text-left font-semibold">Project</th>
                        <th className="px-3 py-1.5 text-left font-semibold">Sub-Portfolio</th>
                        <th className="px-3 py-1.5 text-right font-semibold">Due</th>
                        <th className="px-3 py-1.5 text-right font-semibold">Forecast GL</th>
                        <th className="px-3 py-1.5 text-right font-semibold">Value (hrs)</th>
                        <th className="px-3 py-1.5 text-center font-semibold">Open risks</th>
                        <th className="px-3 py-1.5 text-center font-semibold">Forecast OTIFAC</th>
                        <th className="px-3 py-1.5 text-center font-semibold">Status report</th>
                        <th className="pl-3 pr-4 py-1.5" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {visibleRows.map(p => {
                        const mark = review.reviews[p.id]
                        const overdue = p.status === 'forecast' && p.dueDate && new Date(p.dueDate).getTime() < Date.now()
                        return (
                          <tr key={p.id} className="hover:bg-slate-50/50">
                            <td className="px-4 py-2 w-2">
                              <div className={`w-1.5 h-6 rounded-full ${mark ? 'bg-[#00843D]/30' : overdue || !p.lastMeaningfulUpdate ? 'bg-[#EF3340]' : 'bg-slate-200'}`} />
                            </td>
                            <td className="py-2 pr-3">
                              <Link to={`/projects/${p.id}`} title={p.name} className="block max-w-[20rem] truncate font-medium text-slate-800 hover:text-danone-blue hover:underline">{p.name}</Link>
                              <div className="text-xs text-slate-400 truncate max-w-[20rem]">
                                {p.tier}
                                {!isCurrentMonth && p.status !== 'forecast' && <span className="text-slate-500"> · now {p.status}</span>}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{p.subPortfolio}</td>
                            <td className={`px-3 py-2 whitespace-nowrap text-xs text-right ${overdue ? 'text-[#EF3340] font-semibold' : 'text-slate-500'}`}>
                              {p.dueDate ? formatDate(p.dueDate) : '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-right text-slate-500">
                              {p.actualGoLive ? formatDate(p.actualGoLive) : '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-right tabular-nums text-slate-600">
                              {p.expectedValue ? formatNumber(p.expectedValue) : '-'}
                            </td>
                            <td className="px-3 py-2 text-center"><RiskDots project={p} /></td>
                            <td className="px-3 py-2 text-center">
                              {p.status === 'forecast'
                                ? <OtifBadges project={p} onTimeMs={onTimeMs} minScore={minScore} />
                                : <span className="text-xs text-slate-300">-</span>}
                            </td>
                            <td className="px-3 py-2 text-center whitespace-nowrap">
                              <ReportCell
                                project={p}
                                report={reports[p.id]}
                                month={month}
                                dueAt={monthWindow.dueAt}
                                editable={reportEditable(p.id)}
                                onToggle={() => toggleReportReview(p)}
                              />
                            </td>
                            <td className="pl-3 pr-4 py-2 text-right whitespace-nowrap">
                              {mark ? (
                                <button
                                  onClick={() => toggleReview(p)}
                                  disabled={!isEditable}
                                  title={isEditable ? 'Click to remove the validation' : 'Historical record'}
                                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#00843D]/10 text-[#00843D] text-xs font-semibold ${isEditable ? 'hover:bg-[#00843D]/20' : 'cursor-default'}`}
                                >
                                  <Check className="w-3 h-3" />
                                  Validated{mark.by?.name ? ` by ${mark.by.name}` : ''}{mark.at ? ` · ${formatDate(mark.at)}` : ''}
                                </button>
                              ) : isEditable ? (
                                <button
                                  onClick={() => toggleReview(p)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-slate-200 text-xs font-semibold text-slate-500 hover:border-[#00843D]/40 hover:text-[#00843D]"
                                >
                                  Validate
                                </button>
                              ) : (
                                <span className="text-xs text-slate-400">Not validated</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
                {!isCollapsed && visibleRows.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-3">All validated - hidden by the filter.</p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
